<div class="modal-header border-bottom border-secondary bg-dark text-light">
  <div class="d-flex align-items-center gap-3">
    <h4 class="modal-title text-light mb-0">Data Sync Check</h4>
    <span
      class="badge rounded-pill"
      [style.background-color]="statusColor(overallStatus)"
      [style.color]="'#111'"
      style="font-size: 12px; font-weight: 700; padding: 4px 10px;"
    >
      @switch (overallStatus) {
        @case ('loading') { ↻ Checking… ({{ pendingCount }} remaining) }
        @case ('syncing') { ↻ Aligning mismatches… }
        @case ('ok')      { ✓ In Sync }
        @case ('mismatch'){ ✗ {{ totalMismatches }} Mismatch(es) }
        @case ('error')   { ! Error }
      }
    </span>
  </div>
  <button type="button" class="btn-close btn-close-white pull-right" (click)="modalRef.hide()">
    <span aria-hidden="true" class="visually-hidden">&times;</span>
  </button>
</div>

<div class="modal-body bg-dark text-light" style="min-height: 300px;">

  <!-- Company info header -->
  <div class="mb-3" style="font-size: 13px; color: #94a3b8;">
    <strong>Company ID:</strong> {{ companyId || 'Unknown' }}
    @if (lastChecked) {
      &nbsp;·&nbsp;<strong>Last checked:</strong> {{ lastChecked | date:'medium' }}
    }
  </div>

  <!-- Results table — always shown, rows update as checks complete -->
  <table class="table table-dark table-sm table-hover mb-0" style="font-size: 13px;">
    <thead>
      <tr style="color: #94a3b8; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;">
        <th style="width: 40px; text-align: center;">Status</th>
        <th>Collection</th>
        <th style="text-align: right; width: 100px;">Local (IDB)</th>
        <th style="text-align: right; width: 100px;">Remote (DB)</th>
        <th style="text-align: right; width: 80px;">Diff</th>
      </tr>
    </thead>
    <tbody>
      @for (col of collections; track col.key) {
        <tr [style.opacity]="col.status === 'loading' ? '0.45' : '1'" style="transition: opacity 0.3s ease;">
          <td style="text-align: center; vertical-align: middle;">
            @if (col.status === 'loading') {
              <div class="spinner-border spinner-border-sm text-secondary" role="status" style="width:14px;height:14px;border-width:2px;">
                <span class="visually-hidden">Loading…</span>
              </div>
            } @else if (col.status === 'local-done') {
              <div class="spinner-border spinner-border-sm text-primary" role="status" style="width:14px;height:14px;border-width:2px;">
                <span class="visually-hidden">Fetching remote…</span>
              </div>
            } @else if (col.status === 'syncing') {
              <div class="spinner-border spinner-border-sm" role="status" style="width:14px;height:14px;border-width:2px; color: #a78bfa;">
                <span class="visually-hidden">Syncing…</span>
              </div>
            } @else {
              <span style="font-size: 15px; font-weight: bold;" [style.color]="statusColor(col.status)">
                {{ statusIcon(col.status) }}
              </span>
            }
          </td>
          <td style="vertical-align: middle; font-weight: 500;">{{ col.label }}</td>
          <td style="text-align: right; vertical-align: middle; font-family: monospace;">
            @if (col.local === null) {
              <span class="text-muted" style="font-size: 11px;">waiting…</span>
            } @else {
              {{ col.local }}
            }
          </td>
          <td style="text-align: right; vertical-align: middle; font-family: monospace;">
            @if (col.status === 'loading' || col.status === 'local-done') {
              <span class="text-muted" style="font-size: 11px;">waiting…</span>
            } @else if (col.remote === null) {
              <span class="text-warning">N/A</span>
            } @else {
              {{ col.remote }}
            }
          </td>
          <td style="text-align: right; vertical-align: middle; font-family: monospace;">
            @if (col.status === 'loading' || col.status === 'local-done') {
              <span class="text-muted">—</span>
            } @else if (col.remote === null) {
              <span class="text-muted">—</span>
            } @else if (col.local === col.remote) {
              <span style="color: #4ade80;">0</span>
            } @else {
              <span style="color: #f87171; font-weight: 700;">
                {{ (col.local ?? 0) - (col.remote ?? 0) > 0 ? '+' : '' }}{{ (col.local ?? 0) - (col.remote ?? 0) }}
              </span>
            }
          </td>
        </tr>
      }
    </tbody>
  </table>

  <!-- Top-level error -->
  @if (!isLoading && hasError) {
    <div class="alert alert-warning mt-3">
      <strong>Check failed.</strong> Could not reach the server or query IndexedDB. Make sure you are online and logged in.
    </div>
  }

  @if (overallStatus !== 'loading') {
    <p class="text-muted mt-3 mb-0" style="font-size: 11px; line-height: 1.5;">
      <strong>Note:</strong> A count mismatch may mean records are still queued for sync, were deleted on one side, or the local cache hasn't been refreshed yet.
      A positive diff means more records exist locally than in MongoDB; negative means MongoDB has more.
    </p>
  }
</div>

<div class="modal-footer border-top border-secondary bg-dark d-flex justify-content-between">
  <button type="button" class="btn btn-outline-secondary btn-sm" (click)="runCheck()" [disabled]="overallStatus === 'loading' || overallStatus === 'syncing'">
    <i class="fa-solid fa-rotate-right me-1"></i> Re-check
  </button>
  <button type="button" class="btn btn-secondary btn-sm" (click)="modalRef.hide()">Close</button>
</div>
